import React from "react";
import {
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import { ContractStatusData, SupportStatusData } from "../../types";
import {
  normalizeSupportLevel,
  NO_SUPPORT_CONTRACT,
  SUPPORT_LEVEL_OPTIONS,
} from "../../constants/supportLevel";
import SupportToggle from "./SupportToggle";

interface ContractStatusCardProps {
  data: ContractStatusData;
  supportStatus: SupportStatusData;
  showSupportStatus?: boolean;
  supportStatusReadOnly?: boolean;
  isSupportStatusSaving?: boolean;
  supportStatusError?: string | null;
  onSaveSupportStatus?: (payload: {
    support_level: string;
    support_status: number;
  }) => void;
  onClearSupportStatusError?: () => void;
  onSendContractReminder?: () => void;
  isContractReminderSending?: boolean;
}

const ContractStatusCard: React.FC<ContractStatusCardProps> = ({
  data,
  supportStatus,
  showSupportStatus = true,
  supportStatusReadOnly = false,
  isSupportStatusSaving = false,
  supportStatusError = null,
  onSaveSupportStatus,
  onClearSupportStatusError,
  onSendContractReminder,
  isContractReminderSending = false,
}) => {
  const [isEditingSupport, setIsEditingSupport] = React.useState(false);
  const [canSupport, setCanSupport] = React.useState(supportStatus.canSupport);
  const [supportLevel, setSupportLevel] = React.useState(
    normalizeSupportLevel(supportStatus.supportLevel)
  );
  const [draftCanSupport, setDraftCanSupport] = React.useState(
    supportStatus.canSupport
  );
  const [draftSupportLevel, setDraftSupportLevel] = React.useState(
    normalizeSupportLevel(supportStatus.supportLevel)
  );
  const wasSavingRef = React.useRef(false);

  React.useEffect(() => {
    const normalizedLevel = normalizeSupportLevel(supportStatus.supportLevel);
    setCanSupport(supportStatus.canSupport);
    setSupportLevel(normalizedLevel);
    setDraftCanSupport(supportStatus.canSupport);
    setDraftSupportLevel(normalizedLevel);
    setIsEditingSupport(false);
  }, [supportStatus.canSupport, supportStatus.supportLevel]);

  React.useEffect(() => {
    if (wasSavingRef.current && !isSupportStatusSaving && !supportStatusError) {
      setIsEditingSupport(false);
    }
    wasSavingRef.current = isSupportStatusSaving;
  }, [isSupportStatusSaving, supportStatusError]);

  const handleEdit = () => {
    onClearSupportStatusError?.();
    setDraftCanSupport(canSupport);
    setDraftSupportLevel(supportLevel);
    setIsEditingSupport(true);
  };

  const handleSave = () => {
    onSaveSupportStatus?.({
      support_level: draftCanSupport ? draftSupportLevel : NO_SUPPORT_CONTRACT,
      support_status: draftCanSupport ? 1 : 0,
    });
  };

  const handleCanSupportChange = (value: boolean) => {
    setDraftCanSupport(value);
    if (!value) {
      setDraftSupportLevel(NO_SUPPORT_CONTRACT);
    }
  };

  const handleCancel = () => {
    setDraftCanSupport(canSupport);
    setDraftSupportLevel(supportLevel);
    setIsEditingSupport(false);
  };

  return (
    <div className="contract-status-card">
      <h3 className="contract-status-card__title">Contract Status</h3>

      <div className="contract-status-card__panel">
        <div className="contract-status-card__renewal-row">
          <div className="contract-status-card__renewal-text">
            <span className="contract-status-card__renewal-label">
              {data.renewalDueLabel}
            </span>
            <span className="contract-status-card__renewal-date">
              {data.renewalDueDate}
            </span>
          </div>
          <button
            type="button"
            className="contract-status-card__reminder-btn"
            onClick={onSendContractReminder}
            disabled={isContractReminderSending || !onSendContractReminder}
          >
            {isContractReminderSending ? (
              <Loader variant={LoaderSizes.small} />
            ) : (
              <Iconography
                icon="triangleExclamation"
                size={IconographySizes.Small}
              />
            )}
            Send Reminder
          </button>
        </div>

        <div className="contract-status-card__sla">
          <div className="contract-status-card__sla-meta">
            <span>SLA Contract Validity</span>
            <span>{data.timeLeft}</span>
          </div>
          <div className="contract-status-card__sla-bar">
            <div
              className="contract-status-card__sla-fill"
              style={{ width: `${data.progressPercent}%` }}
            />
          </div>
          <p className="contract-status-card__sla-label">{data.progressLabel}</p>
        </div>

        <div className="contract-status-card__history">
          <div className="contract-status-card__history-item">
            <span className="contract-status-card__history-label">Last Renewed</span>
            <span className="contract-status-card__history-value">
              {data.lastRenewed}
            </span>
          </div>
          <div className="contract-status-card__history-item contract-status-card__history-item--right">
            <span className="contract-status-card__history-label">Last Expired</span>
            <span className="contract-status-card__history-value">
              {data.lastExpired}
            </span>
          </div>
        </div>
      </div>

      {showSupportStatus && (
      <div
        className={`contract-status-card__support ${
          isEditingSupport ? "contract-status-card__support--editing" : ""
        }`}
      >
        <div className="contract-status-card__support-header">
          <div className="contract-status-card__support-header-left">
            <span className="contract-status-card__support-header-icon" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M13 2L4.5 12.5H11L10 22L19.5 10.5H13L13 2Z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <span>Support Status</span>
          </div>
          {isEditingSupport ? (
            <div className="contract-status-card__support-actions">
              <button
                type="button"
                className="contract-status-card__support-action-btn"
                aria-label="Save support status"
                onClick={handleSave}
                disabled={isSupportStatusSaving}
              >
                <Iconography icon="check" size={IconographySizes.Small} />
              </button>
              <button
                type="button"
                className="contract-status-card__support-action-btn"
                aria-label="Cancel editing support status"
                onClick={handleCancel}
                disabled={isSupportStatusSaving}
              >
                <Iconography icon="xmark" size={IconographySizes.Small} />
              </button>
            </div>
          ) : (
            !supportStatusReadOnly && (
            <button
              type="button"
              className="contract-status-card__support-edit-btn"
              aria-label="Edit support status"
              onClick={handleEdit}
            >
              <Iconography icon="pen" size={IconographySizes.Small} />
            </button>
            )
          )}
        </div>

        {supportStatusError && (
          <p className="contract-status-card__support-error">{supportStatusError}</p>
        )}

        {isEditingSupport ? (
          <div className="contract-status-card__support-body contract-status-card__support-body--edit">
            <div className="contract-status-card__support-main">
              <p className="contract-status-card__support-question">
                CAN WE SUPPORT THIS PARTNER NOW?
              </p>
              <div className="contract-status-card__support-toggle-row">
                <SupportToggle
                  isOn={draftCanSupport}
                  onChange={handleCanSupportChange}
                  disabled={isSupportStatusSaving}
                />
                <span className="contract-status-card__support-toggle-label">
                  {draftCanSupport ? "Yes" : "No"}
                </span>
              </div>
            </div>
            <div className="contract-status-card__support-level contract-status-card__support-level--edit">
              <label
                className="contract-status-card__support-level-label"
                htmlFor="support-level-select"
              >
                Support Level
              </label>
              <select
                id="support-level-select"
                className="contract-status-card__support-level-select"
                value={draftSupportLevel}
                onChange={(event) => setDraftSupportLevel(event.target.value)}
                disabled={isSupportStatusSaving || !draftCanSupport}
              >
                {SUPPORT_LEVEL_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="contract-status-card__support-body">
            <div className="contract-status-card__support-main">
              <p className="contract-status-card__support-question">
                CAN WE SUPPORT THIS PARTNER NOW?
              </p>
              <p className="contract-status-card__support-answer">
                {canSupport ? "Yes" : "No"}
              </p>
            </div>
            <div className="contract-status-card__support-level">
              <span className="contract-status-card__support-level-label">
                Support Level
              </span>
              <span className="contract-status-card__support-level-value">
                {supportLevel}
              </span>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default ContractStatusCard;
